import { Box, makeStyles, Typography } from "@material-ui/core";
import * as React from "react";
import { NumericFormat } from "react-number-format";

import { fontFamilies } from "@/designTokens";
import theme from "@/theme";

import { BottomSheet } from "../BottomSheet";
import { CustomButton } from "../Button";
import { CustomChip } from "../Chip";
import { FormErrorText } from "../FormErrorText";

import { FormRow } from "./FormRow";

const useStyles = makeStyles(() => ({
  entry: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: 8,
    padding: "16px 0",
    borderBottom: `1px solid ${theme.palette.semantic["border/default"]}`,
  },
  input: {
    width: "min(70%, 280px)",
    minWidth: 0,
    background: "none",
    border: 0,
    outline: 0,
    fontFamily: fontFamilies.number,
    fontSize: 32,
    fontWeight: 600,
    textAlign: "right",
    color: theme.palette.text.primary,
    "&:focus-visible": { boxShadow: `0 2px ${theme.palette.primary.main}` },
  },
  unit: { fontSize: 14, color: theme.palette.text.secondary },
  quick: {
    "& .MuiChip-root": { fontFamily: fontFamilies.number },
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 8,
    marginTop: 8,
  },
  quickLabel: { textAlign: "center", color: theme.palette.text.secondary, fontSize: 12, marginTop: 16 },
}));

export const NumberEntry: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  unit: string;
  quickValues?: number[];
  autoFocus?: boolean;
  error?: string;
}> = ({ label, value, onChange, onBlur, unit, quickValues, autoFocus, error }) => {
  const classes = useStyles();
  return (
    <Box>
      <Box className={classes.entry}>
        <NumericFormat
          aria-label={label}
          className={classes.input}
          value={value}
          thousandSeparator=","
          allowLeadingZeros
          inputMode="numeric"
          autoFocus={autoFocus}
          placeholder="0"
          onBlur={onBlur}
          isAllowed={(values) => values.value.length <= 10}
          onValueChange={(values) => onChange(values.formattedValue)}
          aria-invalid={!!error}
        />
        <span className={classes.unit}>{unit}</span>
      </Box>
      {error && <FormErrorText>{error}</FormErrorText>}
      {quickValues && (
        <>
          <Typography className={classes.quickLabel}>よくある金額</Typography>
          <Box className={classes.quick}>
            {quickValues.map((n) => (
              <CustomChip key={n} label={`${n.toLocaleString()}${unit}`} onClick={() => onChange(n.toLocaleString())} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export const NumberInputField = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    unit: string;
    quickValues?: number[];
    error?: string;
  }
>(({ label, value, onChange, onBlur, unit, quickValues, error }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const close = () => {
    setOpen(false);
    onBlur?.();
  };
  const start = () => {
    setDraft(value);
    setOpen(true);
  };
  return (
    <>
      <FormRow
        ref={ref}
        label={label}
        value={value ? `${Number(value.replace(/,/g, "")).toLocaleString()}${unit}` : undefined}
        error={error}
        onClick={start}
        onBlur={onBlur}
      />
      <BottomSheet
        open={open}
        title={label}
        onOpen={start}
        onClose={close}
        maxHeightPercent={70}
        footer={
          <CustomButton
            buttonSize="L"
            type="button"
            disabled={!draft.trim()}
            onClick={() => {
              onChange(draft);
              close();
            }}
          >
            決定
          </CustomButton>
        }
      >
        <NumberEntry label={label} value={draft} onChange={setDraft} unit={unit} quickValues={quickValues} autoFocus />
      </BottomSheet>
    </>
  );
});

NumberInputField.displayName = "NumberInputField";
